<template>
  <span class="code-chip">
    <code class="code-chip__code">{{ displayCode }}</code>
    <base-button
      v-if="copyable"
      class="btn btn-link p-0 code-chip__copy"
      icon
      simple
      size="sm"
      type="primary"
      :title="'Copy ' + label"
      @click.stop="copy"
    >
      <i class="tim-icons el-icon-copy-document"></i>
    </base-button>
  </span>
</template>

<script lang="ts">
export default { name: 'code-chip' }
</script>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue'

// Monospace code chip with a copy button. House pattern for gift card codes,
// promo codes, and any other value staff copy verbatim. Numeric codes are
// grouped in fours for reading; the copied value is always the raw code.
const props = defineProps({
  code: { type: String, default: '' },
  // Group digits in fours when the code is numeric (gift card style).
  group: { type: Boolean, default: true },
  copyable: { type: Boolean, default: true },
  // Noun used in the copy notification and tooltip, for example "gift card code".
  label: { type: String, default: 'code' }
})

const { proxy } = getCurrentInstance() as any

const displayCode = computed(() => {
  if (!props.code) return ''
  if (!props.group || isNaN(Number(props.code))) return props.code
  const parts = props.code.match(/.{1,4}/g)
  return parts ? parts.join(' ') : props.code
})

const notification = (message: string, type: string) => ({
  message,
  type,
  icon: type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc',
  horizontalAlign: 'right',
  verticalAlign: 'top',
  timeout: 2000
})

async function copy() {
  const done = props.label.charAt(0).toUpperCase() + props.label.slice(1) + ' copied'
  try {
    await navigator.clipboard.writeText(props.code)
    proxy.$notify(notification(done, 'success'))
  } catch (err) {
    try {
      const textArea = document.createElement('textarea')
      textArea.value = props.code
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      proxy.$notify(notification(done, 'success'))
    } catch (fallbackErr) {
      proxy.$notify(notification('Could not copy', 'warning'))
    }
  }
}
</script>

<style lang="scss">
.code-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.code-chip__code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.5px;
  font-weight: 500;
  white-space: nowrap;
}

.code-chip__copy {
  opacity: 0.5;
  transition: opacity 0.2s;
  color: #c8c8c8 !important;

  &:hover {
    opacity: 1;
    color: #c8c8c8 !important;
  }
}

.white-content .code-chip__code {
  background-color: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.8);
}
</style>
